import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import LangToggle from './LangToggle';
import { Avatar } from './UI';
import { Home, Trophy, Swords, User, Shield, MessageSquare, BarChart2 } from 'lucide-react';
import api from '../api/axios';
import { PLAYER_MESSAGES_POLL_MS } from '../constants/messagesPoll';

export default function Navbar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const settings = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  const leaderboardVisible = settings?.leaderboard_visible !== 'false' || user?.role === 'admin';
  const tournamentsVisible = settings?.tournaments_enabled !== 'false';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try {
        const path = isAdmin ? '/admin/feedback/unread-count' : '/messages/unread-count';
        const res = await api.get(path);
        setUnreadCount(res.data.count || 0);
      } catch (_) {}
    };
    fetchUnread();
    // Same cadence as player Messages page so badge and inbox list stay aligned
    const pollMs =
      location.pathname === '/messages' && !isAdmin ? PLAYER_MESSAGES_POLL_MS : 20000;
    const interval = setInterval(fetchUnread, pollMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchUnread();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, isAdmin, location.pathname]);

  const MessagesIcon = (
    <span className="relative inline-flex">
      <MessageSquare size={20} />
      {unreadCount > 0 && (
        <span className="absolute -top-1.5 -end-1.5 min-w-[16px] h-4 rounded-full flex items-center justify-center text-[9px] font-bold text-white px-0.5"
          style={{ background: '#EF4444', lineHeight: 1 }}>
          {unreadCount}
        </span>
      )}
    </span>
  );

  const isAr = t('language') === 'العربية';
  const navItems = [
    ...(!isAdmin ? [{ to: '/dashboard', icon: <Home size={20} />, label: t('nav_home'), shortLabel: t('nav_home') }] : []),
    ...(!isAdmin ? [{ to: '/matches', icon: <Swords size={20} />, label: t('matches'), shortLabel: isAr ? 'مباريات' : 'Matches' }] : []),
    ...(!isAdmin && tournamentsVisible ? [{ to: '/tournaments', icon: <Trophy size={20} />, label: t('tournaments'), shortLabel: isAr ? 'بطولات' : 'Tourn.' }] : []),
    ...(leaderboardVisible ? [{ to: '/leaderboard', icon: <BarChart2 size={20} />, label: t('leaderboard'), shortLabel: isAr ? 'ترتيب' : 'Rank' }] : []),
    { to: '/messages', icon: MessagesIcon, label: t('messages'), shortLabel: isAr ? 'رسائل' : 'Inbox' },
    { to: '/profile', icon: <User size={20} />, label: t('profile'), shortLabel: isAr ? 'ملفي' : 'Profile' },
  ];

  if (!user) return null;

  return (
    <>
      {/* Top Navbar — Desktop */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 z-50 items-center justify-between px-6 py-3 border-b border-white/10"
        style={{ background: 'rgba(10,14,26,0.95)', backdropFilter: 'blur(12px)' }}>

        <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
          <span className="text-xl">🏓</span>
          <span className="font-heading font-black text-lg tracking-widest"
            style={{ background: 'linear-gradient(135deg, #fff 30%, #F5C518 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            MATCHMATE
          </span>
        </div>

        <div className="flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive ? 'text-accent bg-accent/10 border border-accent/20' : 'text-text-secondary hover:text-white hover:bg-white/5'
                }`
              }>
              {item.icon}<span>{item.label}</span>
            </NavLink>
          ))}
          {user.role === 'admin' && (
            <NavLink to="/admin"
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive ? 'text-accent bg-accent/10 border border-accent/20' : 'text-text-secondary hover:text-white hover:bg-white/5'
                }`
              }>
              <Shield size={20} /><span>{t('admin_panel')}</span>
            </NavLink>
          )}
        </div>

        <div className="flex items-center gap-2">
          <LangToggle />
          <div className="cursor-default">
            <Avatar name={user.name} avatar={user.avatar} size="sm" playerId={user.id} />
          </div>
        </div>
      </nav>

      {/* Bottom Navigation — Mobile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-white/10"
        style={{ background: 'rgba(10,14,26,0.97)', backdropFilter: 'blur(12px)' }}>
        <div className="flex items-center justify-around py-1.5 px-0">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-xl transition-all duration-200 flex-1 max-w-[64px] ${
                  isActive ? 'text-accent' : 'text-text-secondary'
                }`
              }>
              {item.icon}
              <span className="text-[10px] font-medium leading-tight text-center w-full truncate">{item.shortLabel}</span>
            </NavLink>
          ))}
          {user.role === 'admin' && (
            <NavLink to="/admin"
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-xl transition-all flex-1 max-w-[64px] ${
                  isActive ? 'text-accent' : 'text-text-secondary'
                }`
              }>
              <Shield size={20} />
              <span className="text-[10px] font-medium leading-tight">Admin</span>
            </NavLink>
          )}
        </div>
      </nav>
    </>
  );
}
